import { useEffect, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Plus, Send, Trash2, Sparkles, Loader2, MessageSquare, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const STARTERS = [
  "Draft a friendly rent reminder for a tenant who is 3 days late.",
  "What should I include in a move-in inspection checklist?",
  "Help me write a lease renewal offer with a 4% increase.",
  "How should I respond to a tenant complaint about a noisy neighbour?",
];

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`ai-msg-${role}`}
    >
      {!isUser && (
        <div className="h-8 w-8 rounded-full bg-ink text-gold flex items-center justify-center shrink-0 ring-1 ring-gold/40">
          <Bot className="h-4 w-4" />
        </div>
      )}
      <div
        className={`max-w-[78%] px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-ink text-ivory rounded-2xl rounded-br-sm whitespace-pre-wrap"
            : "bg-white border border-bone text-ink rounded-2xl rounded-bl-sm shadow-sm ai-markdown"
        }`}
      >
        {isUser ? content : (
          <ReactMarkdown
            components={{
              p: (props) => <p className="mb-2 last:mb-0" {...props} />,
              ul: (props) => <ul className="list-disc pl-5 space-y-1 my-2" {...props} />,
              ol: (props) => <ol className="list-decimal pl-5 space-y-1 my-2" {...props} />,
              strong: (props) => <strong className="font-semibold text-ink" {...props} />,
              em: (props) => <em className="italic" {...props} />,
              code: (props) => <code className="bg-bone/70 px-1 py-0.5 rounded text-[12px]" {...props} />,
              h1: (props) => <h3 className="font-display text-lg tracking-tight mt-1 mb-1.5" {...props} />,
              h2: (props) => <h4 className="font-semibold text-base mt-1 mb-1" {...props} />,
              h3: (props) => <h5 className="font-semibold text-sm mt-1 mb-1" {...props} />,
              a: (props) => <a className="text-gold underline hover:no-underline" target="_blank" rel="noreferrer" {...props} />,
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
      {isUser && (
        <div className="h-8 w-8 rounded-full bg-gold/20 text-ink flex items-center justify-center shrink-0 ring-1 ring-gold/40">
          <UserIcon className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}

export default function AiAssistantPage() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const listEndRef = useRef(null);

  const loadSessions = async (preferId = null) => {
    const { data } = await api.get("/ai/chat/sessions");
    setSessions(data);
    if (preferId) setActiveId(preferId);
    else if (data.length > 0 && !activeId) setActiveId(data[0].id);
    setLoading(false);
  };

  const loadMessages = async (id) => {
    if (!id) { setMessages([]); return; }
    const { data } = await api.get(`/ai/chat/sessions/${id}`);
    setMessages(data.messages || []);
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => { loadMessages(activeId); }, [activeId]);

  useEffect(() => {
    if (listEndRef.current) listEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const newChat = async () => {
    const { data } = await api.post("/ai/chat/sessions", {});
    await loadSessions(data.id);
    setMessages([]);
  };

  const removeSession = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/ai/chat/sessions/${confirmDelete.id}`);
      const remaining = sessions.filter((s) => s.id !== confirmDelete.id);
      setSessions(remaining);
      if (activeId === confirmDelete.id) {
        setActiveId(remaining[0]?.id || null);
        setMessages([]);
      }
      setConfirmDelete(null);
      toast.success("Chat deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);

    // Ensure we have a session
    let sid = activeId;
    if (!sid) {
      try {
        const { data } = await api.post("/ai/chat/sessions", {});
        sid = data.id;
        setActiveId(sid);
        setSessions((s) => [data, ...s]);
      } catch (e) {
        toast.error("Could not start chat");
        setSending(false);
        return;
      }
    }

    // Optimistic user bubble
    const now = new Date().toISOString();
    setMessages((m) => [...m, { id: `tmp-${now}`, role: "user", content: text, created_at: now }]);

    try {
      const { data } = await api.post(`/ai/chat/sessions/${sid}/messages`, { text });
      setMessages((m) => {
        // Replace tmp user + append assistant
        const clean = m.filter((x) => x.id !== `tmp-${now}`);
        return [...clean, data.user_message, data.assistant_message];
      });
      // Refresh sessions list so titles/updated_at reorder
      loadSessions(sid);
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== `tmp-${now}`));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI is unavailable, please try again");
    }
    setSending(false);
  };

  return (
    <AppLayout
      title="AI Assistant"
      subtitle="Ask Rojas AI for drafts, checklists and property-management guidance"
      actions={
        <Button
          data-testid="ai-new-chat-btn"
          onClick={newChat}
          className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold gap-2"
        >
          <Plus className="h-4 w-4" /> New chat
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 h-[calc(100vh-14rem)]">
        {/* Sidebar — sessions */}
        <Card className="rounded-none border-bone p-0 flex flex-col overflow-hidden" data-testid="ai-sessions-panel">
          <div className="px-4 py-3 border-b border-bone">
            <div className="micro-label text-gold">Conversations</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
            ) : sessions.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500 leading-relaxed">
                No conversations yet. Start a new chat, or pick a prompt on the right to begin.
              </div>
            ) : (
              <ul>
                {sessions.map((s) => (
                  <li key={s.id}>
                    <div
                      data-testid={`ai-session-${s.id}`}
                      onClick={() => setActiveId(s.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveId(s.id); } }}
                      className={`group w-full text-left px-4 py-3 border-b border-bone/60 flex items-start gap-2 transition-colors cursor-pointer ${
                        activeId === s.id ? "bg-ink text-ivory" : "hover:bg-gold/5 text-ink"
                      }`}
                    >
                      <MessageSquare className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${activeId === s.id ? "text-gold" : "text-slate-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${activeId === s.id ? "font-medium" : ""}`}>{s.title}</div>
                        <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${activeId === s.id ? "text-gold/70" : "text-slate-400"}`}>
                          {fmtTime(s.updated_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`ai-session-delete-${s.id}`}
                        onClick={(ev) => { ev.stopPropagation(); setConfirmDelete(s); }}
                        className={`opacity-0 group-hover:opacity-100 transition-opacity ${activeId === s.id ? "text-ivory/70 hover:text-rose-300" : "text-slate-400 hover:text-rose-500"}`}
                        aria-label="Delete chat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* Chat panel */}
        <Card className="rounded-none border-bone p-0 flex flex-col overflow-hidden" data-testid="ai-chat-panel">
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-ivory/40">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-8">
                <div className="h-14 w-14 rounded-full bg-ink text-gold flex items-center justify-center ring-1 ring-gold/40 mb-4">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div className="micro-label text-gold mb-2">Rojas AI</div>
                <h3 className="font-display text-2xl tracking-tight text-ink mb-2">How can I help today?</h3>
                <p className="text-sm text-slate-500 max-w-md mb-6 leading-relaxed">
                  Draft correspondence, ideate checklists, or work through a tricky tenant situation. Everything stays private to your account.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-2xl">
                  {STARTERS.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`ai-starter-${i}`}
                      onClick={() => setInput(s)}
                      className="text-left border border-bone bg-white p-3 hover:border-gold hover:bg-gold/5 transition-colors text-xs text-slate-700 leading-relaxed"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((m) => <Bubble key={m.id} role={m.role} content={m.content} />)}
                {sending && (
                  <div className="flex gap-3 justify-start">
                    <div className="h-8 w-8 rounded-full bg-ink text-gold flex items-center justify-center ring-1 ring-gold/40">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="bg-white border border-bone rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-500 flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                    </div>
                  </div>
                )}
                <div ref={listEndRef} />
              </>
            )}
          </div>

          <form onSubmit={submit} className="border-t border-bone bg-white p-4 flex items-end gap-3" data-testid="ai-chat-form">
            <Textarea
              data-testid="ai-chat-input"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Ask anything — press Enter to send, Shift+Enter for a new line."
              className="resize-none border-bone focus-visible:ring-gold/40"
              disabled={sending}
            />
            <Button
              type="submit"
              data-testid="ai-chat-send"
              disabled={sending || !input.trim()}
              className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink h-11 px-5 uppercase tracking-[0.2em] text-xs font-semibold gap-2"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send
            </Button>
          </form>
        </Card>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
            <AlertDialogDescription>All messages in this conversation will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="ai-session-delete-confirm" onClick={removeSession} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
